import { preventImplicitSubmit } from "../../../../../app/javascript/keyboard/form_keys"
import { fetchVariantDetails } from "utils/variant_details"
import {Controller} from "@hotwired/stimulus"
import {showToast} from "utils/toast";

export default class extends Controller {
    static targets = ["form", "itemTemplate", "itemsContainer", "itemsPanel", "subtotal", "tax", "total"]
    static values = {status: String, itemVariantsUrl: String, taxesUrl: String, postUrl: String}

    connect() {
        this.itemVariants = []
        this.itemContent = this.itemTemplateTarget.innerHTML

        this.supplierInput = this.formTarget.querySelector("input[name='debit_note[supplier_id]']")
        this.supplierSelectInput = this.formTarget.querySelector("#debit_note_supplier_id_hw_combobox")
        this.taxModeInput = this.formTarget.querySelector("select[name='debit_note[tax_mode]']")
        this.currencyInput = this.formTarget.querySelector("select[name='debit_note[currency]']")
        this.postButton = this.formTarget.querySelector("#btn_post")

        this.isDirty = false

        this.formTarget.addEventListener("input", () => { this.isDirty = true })
        this.formTarget.addEventListener("change", () => { this.isDirty = true })
        this.formTarget.addEventListener("keydown", preventImplicitSubmit)


        if (this.supplierInput?.value) {
            this.loadSupplierData(this.supplierInput.value).then(() => this.calculateDebitNote())
        } else if (this.supplierSelectInput) {
            this.supplierSelectInput.focus()
        }
    }

    addItem(event) {
        if (event) event.preventDefault()
        const content = this.itemContent.replace(/NEW_RECORD/g, new Date().getTime())
        this.itemsContainerTarget.insertAdjacentHTML("beforeend", content)
        const lastItem = this.itemsContainerTarget.querySelector("tr.item-container:last-child")
        this.configureVariantPicker(lastItem)
        const firstInput = lastItem.querySelector("input")
        if (firstInput) firstInput.focus()
    }

    removeItem(event) {
        event.preventDefault()
        event.target.closest(".item-container").remove()
        this.calculateDebitNote()
    }

    clearItems() {
        this.itemsContainerTarget.innerHTML = ""
    }

    changeSupplier(event) {
        if (event) event.preventDefault()
        this.clearItems()

        if (!this.supplierInput.value) {
            this.itemsPanelTarget.classList.add("d-none")
            return
        }

        this.loadSupplierData(this.supplierInput.value).then(() => this.addItem())
    }

    configureVariantPicker(item) {
        const fieldset = item.querySelector("[data-hw-combobox-async-src-value]")
        if (fieldset) fieldset.dataset.hwComboboxAsyncSrcValue = `${this.itemVariantsUrlValue}?supplier_id=${encodeURIComponent(this.supplierInput?.value || "SUPPLIER_ID")}`
    }

    async loadSupplierData(supplierId) {
        this.itemVariants = []
        this.itemsContainerTarget.querySelectorAll("tr.item-container").forEach(item => this.configureVariantPicker(item))
        const response = await fetch(this.taxesUrlValue, { headers: { Accept: "application/json" }, credentials: "same-origin" })
        if (!response.ok) throw new Error("Unable to load taxes")
        this.taxes = await response.json()
        const ids = [...this.itemsContainerTarget.querySelectorAll("input[name$='[item_variant_id]']")].map(input => input.value).filter(Boolean)
        await Promise.all(ids.map(id => this.loadVariant(id, supplierId)))
        this.itemsPanelTarget.classList.remove("d-none")
    }

    async loadVariant(id, supplierId = this.supplierInput.value) {
        const existing = this.itemVariants.find(variant => variant.id === Number(id))
        if (existing) return existing
        const variant = await fetchVariantDetails(`${this.itemVariantsUrlValue}?supplier_id=${encodeURIComponent(supplierId)}`, id)
        if (this.supplierInput.value === String(supplierId)) this.itemVariants.push(variant)
        return variant
    }

    selectVariant(event) {
        if (event) event.preventDefault()
        setTimeout(async () => {
            const item = event.target.closest("tr.item-container")
            const variantInput = item.querySelector("input[name$='[item_variant_id]']")
            const id = variantInput.value
            if (!id) return
            try {
                const supplierId = this.supplierInput.value
                const variant = await this.loadVariant(id, supplierId)
                if (!item.isConnected || variantInput.value !== id || this.supplierInput.value !== supplierId) return
                const unitPriceInput = item.querySelector("input[name$='[unit_price]']")
                unitPriceInput.value = variant.price
                unitPriceInput.dispatchEvent(new Event("change", { bubbles: true }))
                if (item.parentElement.lastElementChild === item) this.addItem()
            } catch (error) { console.error(error) }
        }, 10)
    }

    calculateDebitNote(event) {
        if (event) event.preventDefault()

        const items = this.collectItems()
        const taxMode = this.taxModeInput.value
        let subtotal = 0
        let totalTax = 0
        let grossTotal = 0

        items.forEach((item) => {
            const result = this.calculateLine(item, taxMode)
            if (!result) return
            subtotal += result.subtotal
            totalTax += result.tax
            grossTotal += result.total
            this.renderLine(item.node, result)
        })

        this.renderTotals(this.round(subtotal), this.round(totalTax), this.round(grossTotal))
    }

    collectItems() {
        const nodes = this.itemsContainerTarget.querySelectorAll("tr.item-container")

        return [...nodes].map((node) => {
            const variantId = Number(node.querySelector("input[name$='[item_variant_id]']").value)
            const quantity = Number(node.querySelector("input[name$='[quantity]']").value)
            const unitPrice = Number(node.querySelector("input[name$='[unit_price]']").value)

            if (!variantId || quantity <= 0 || unitPrice < 0) return null

            const variant = this.itemVariants.find((entry) => entry.id === variantId)
            if (!variant) return null

            return {node, quantity, unitPrice, variant}
        }).filter(Boolean)
    }

    calculateLine(item, taxMode) {
        const rawBase = item.unitPrice * item.quantity
        const roundedBase = this.round(rawBase)
        const applicableTaxes = this.resolveTaxesForVariant(item.variant).sort((a, b) => (a.sequence || 0) - (b.sequence || 0))

        if (!taxMode || taxMode === "no_tax" || applicableTaxes.length === 0) {
            return {subtotal: roundedBase, tax: 0, total: roundedBase, taxes: []}
        }

        let taxes = []
        let lineTaxTotal = 0

        if (taxMode === "tax_exclusive") {
            let runningBase = rawBase

            applicableTaxes.forEach((tax) => {
                const rate = Number(tax.rate) / 100
                const baseAmount = tax.compound ? runningBase : rawBase
                const taxAmount = this.round(baseAmount * rate)

                taxes.push({...tax, base: this.round(baseAmount), amount: taxAmount})
                lineTaxTotal += taxAmount
                if (tax.compound) runningBase += taxAmount
            })

            return {subtotal: roundedBase, tax: this.round(lineTaxTotal), total: this.round(roundedBase + lineTaxTotal), taxes}
        }

        if (taxMode === "tax_inclusive") {
            let netBase = rawBase
            const reversed = [...applicableTaxes].reverse()

            reversed.forEach((tax) => {
                const rate = Number(tax.rate) / 100
                const divisor = 1 + rate
                const baseWithoutTax = netBase / divisor
                const taxAmount = this.round(netBase - baseWithoutTax)

                taxes.push({...tax, base: this.round(baseWithoutTax), amount: taxAmount})
                netBase = baseWithoutTax
                lineTaxTotal += taxAmount
            })

            taxes.reverse()
            return {subtotal: this.round(rawBase - lineTaxTotal), tax: this.round(lineTaxTotal), total: roundedBase, taxes}
        }

        return {subtotal: roundedBase, tax: 0, total: roundedBase, taxes: []}
    }

    renderLine(node, result) {
        const formatter = new Intl.NumberFormat("en-AU", {style: "currency", currency: this.currencyCode()})
        const taxCodesEl = node.querySelector("[data-finance-debit-note-line-target='taxCodes']")
        const taxAmountEl = node.querySelector("[data-finance-debit-note-line-target='taxAmount']")
        const lineTotalEl = node.querySelector("[data-finance-debit-note-line-target='lineTotal']")

        if (taxCodesEl) taxCodesEl.innerText = result.taxes.map((tax) => tax.code).join(", ") || "-"
        if (taxAmountEl) taxAmountEl.innerText = formatter.format(result.tax)
        if (lineTotalEl) lineTotalEl.innerText = formatter.format(result.total)
    }

    resolveTaxesForVariant(variant) {
        const unique = {}
        const results = []

        this.taxes.forEach((tax) => {
            if (variant.taxes.includes(tax.id) && !unique[tax.id]) {
                unique[tax.id] = true
                results.push(tax)
            }
        })

        return results
    }

    renderTotals(subtotal, tax, total) {
        const formatter = new Intl.NumberFormat("en-AU", {style: "currency", currency: this.currencyCode()})
        this.subtotalTarget.innerText = formatter.format(subtotal)
        this.taxTarget.innerText = formatter.format(tax)
        this.totalTarget.innerText = formatter.format(total)
    }

    round(amount) {
        return Math.round((amount + Number.EPSILON) * 100) / 100
    }

    currencyCode() {
        return this.currencyInput.value
    }

    post(event) {
        if (event) event.preventDefault()
        if (this.isDirty) {
            showToast("Please save changes before posting", "warning")
            return
        }

        fetch(this.postUrlValue, {
            method: "POST",
            headers: {
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
                "Accept": "application/json"
            },
            credentials: "same-origin"
        })
            .then(async (response) => {
                const data = await response.json()
                if (!response.ok) throw new Error(data.error || "Failed to post debit note")
                showToast("Posted successfully", "success")
                Turbo.visit(window.location.href)
            })
            .catch((error) => showToast(error.message, "danger"))
    }
}
